'use client'

import { animate } from 'motion/react'
import Image from 'next/image'
import { memo, useEffect, useRef } from 'react'
import { T } from '@/components/t'
import { OfficialNumbers } from '@/features/official-numbers/official-number'
import { cn } from '@/utils/styles/cn'

export const HomePeers = memo(() => {
    const countRef = useRef<HTMLParagraphElement>(null)

    useEffect(() => {
        const isMobile = window.innerWidth < 768

        const node = countRef.current

        const controls = animate(0, OfficialNumbers.USERS, {
            // Must be shorter than the duration it takes for the map to load on mobile on the home page
            duration: isMobile ? 1.5 : 2,
            delay: 0.3,
            ease: 'easeInOut',
            onUpdate(value) {
                if (!node) return

                // separate thousands with a dot
                node.textContent = Math.round(value).toLocaleString('de-DE')
            },
        })

        return () => controls.stop()
    }, [])

    return (
        <div className="w-fit flex items-center rounded-full bg-[#f5f5f5] py-2 pl-2.5 pr-3">
            <div className="flex items-center max-w-[54px] mr-1.5">
                <HomePeersProfile src="/_images/home/hero/user-1.png" />

                <HomePeersProfile
                    src="/_images/home/hero/user-2.png"
                    className="relative left-[-9px]"
                />

                <HomePeersProfile
                    src="/_images/home/hero/user-3.png"
                    className="relative left-[-18px]"
                />
            </div>

            <div className="flex items-center relative top-[1px]">
                <T
                    span
                    className="font-semibold text-[12px] whitespace-nowrap leading-[10px] border-b-[3px] border-b-black/0"
                    t={['home.hero.socialProofBeforeNumber', { userCount: '1.500.000' }]}
                />

                <p
                    className="font-semibold text-[12px] mx-1 min-w-[59px] max-w-[59px] text-center relative top-[0.5px]
                        overflow-visible border-b-neotaste border-b-[3px] leading-[10px]"
                    ref={countRef}
                />

                <T
                    span
                    className="font-semibold text-[12px] whitespace-nowrap leading-[10px] border-b-[3px] border-b-black/0"
                    t={['home.hero.socialProofAfterNumber', { userCount: '1.500.000' }]}
                />
            </div>
        </div>
    )
})

HomePeers.displayName = 'HomePeers'

interface IHomePeersProfileProps {
    className?: string
    src: string
}

const HomePeersProfile = ({ className, src }: IHomePeersProfileProps) => {
    return (
        <div
            className={cn(
                'rounded-full min-w-6 min-h-6 border-white border-[1px] overflow-hidden',
                className
            )}
        >
            <Image
                alt="NeoTaste User"
                src={src}
                height={48}
                width={48}
                className="min-h-6 max-h-6 min-w-6 max-w-6"
            />
        </div>
    )
}
